import React, { useState, useEffect } from "react";

import {
    Box,
    Typography,
    Modal,
    IconButton,
    Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { apiGetCommunityCreator } from "../../services/apiGetCommunityCreator";
import { useCommunityId } from "../../hooks/useCommunityId";

import { creatorIsAdmin } from "../../utils/adminManager";

import { Loader } from "./Loader";

/*
 * This modal displays the details of a community creator
 * such as their name, email, and other relevant information.
 * It is only accessible to admin users.
 */
export const CommunityCreatorDetailsModal = ({
    isOpen,
    onClose,
    creatorId,
}) => {
    const [selectedCreator, setSelectedCreator] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const isAdmin = creatorIsAdmin();
    const communityId = useCommunityId();

    useEffect(() => {
        const fetchCreatorData = async () => {
            if (!isOpen || !creatorId) return;

            try {
                setIsLoading(true);
                setSelectedCreator(null);
                const creatorData = await apiGetCommunityCreator(communityId, creatorId);
                setSelectedCreator(creatorData);
            } catch (error) {
                console.error("Erro ao buscar dados do creator:", error);
                setSelectedCreator(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCreatorData();
    }, [isOpen, creatorId]);

    if (!isAdmin || !isOpen) return null;

    const handleClose = () => {
        setSelectedCreator(null);
        onClose();
    };
    return (
        <Modal
            open={isOpen}
            onClose={handleClose}
            aria-labelledby="creator-modal-title"
            aria-describedby="creator-modal-description"
        >
            <Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: { xs: "90%", sm: 600 },
                    maxHeight: "90vh",
                    overflow: "auto",
                    bgcolor: "background.paper",
                    border: "2px solid",
                    borderColor: "var(--primary-color)",
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 4,
                }}
            >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                    <Typography id="creator-modal-title" variant="h6" component="h2">
                        Detalhes do Membro
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>

                {isLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <Loader />
                    </Box>
                ) : selectedCreator ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Nome
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                {selectedCreator.nome || "Não informado"}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Email
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                {selectedCreator.email || "Não informado"}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Telefone
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                {selectedCreator.telefone || "Não informado"}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Data de Nascimento
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                {selectedCreator.datanascimento
                                    ? new Date(selectedCreator.datanascimento).toLocaleDateString("pt-BR")
                                    : "Não informado"}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Unidade
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                {selectedCreator.franchise?.name || "Não informado"}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Instagram
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                {selectedCreator.instagram ? `@${selectedCreator.instagram}` : "Não informado"}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Instagram da Unidade
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                {selectedCreator.franchise?.instagram ||
                                    selectedCreator.communityFranchise?.franchiseInstagram
                                    ? `@${selectedCreator.franchise?.instagram || selectedCreator.communityFranchise?.franchiseInstagram}`
                                    : "Não informado"}
                            </Typography>
                        </Grid>
                    </Grid>
                ) : (
                    <Typography color="error">
                        Erro ao carregar dados do membro.
                    </Typography>
                )}
            </Box>
        </Modal>
    );
};
